import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import instance from "services/api";
import { useSession } from "hooks/useSession";
import { mfaSetupDismissed } from "@/utils/mfaSetupDismissed";
import type { MfaFactor, TotpEnrollmentResponse } from "../types";

// useMfaFactors (hooks/useMfaFactors.ts) reads via refine's useCustom, whose
// cache key we don't control directly — matching on the URL fragment avoids
// having to reconstruct refine's internal key shape.
const invalidateMfaFactors = (queryClient: QueryClient) =>
  queryClient
    .invalidateQueries({
      predicate: (query) =>
        JSON.stringify(query.queryKey).includes("mfa/factors"),
    })
    .catch((error) => {
      console.error("Failed to refresh MFA factors:", error);
    });

// Every mutation for the 2FA section on Profile settings (/settings), bundled in one hook.
// Each invalidates the factors query on success — the spec calls this out
// explicitly, since enabling a 2nd factor changes which one is `preferred`.
export const useMfaEnrollment = () => {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const invalidate = () => invalidateMfaFactors(queryClient);
  // Once any factor is enabled, permanently suppress the "Enable 2FA?" nudge —
  // otherwise disabling it later (e.g. switching factors) would flip
  // noFactorEnabled back to true and pop the nudge right back up.
  const invalidateAndMarkEnabled = () => {
    invalidate();
    if (user) {
      mfaSetupDismissed.setDismissed(user.id);
    }
  };

  const startTotp = useMutation({
    mutationFn: async () => {
      const { data } = await instance.post<TotpEnrollmentResponse>(
        "/api/users/me/mfa/factors/totp"
      );
      return data;
    },
  });

  const confirmTotp = useMutation({
    mutationFn: (code: string) =>
      instance.post("/api/users/me/mfa/factors/totp/verification", { code }),
    onSuccess: invalidateAndMarkEnabled,
  });

  // BE (MED-6927) split phone verification out of the SMS factor itself:
  // register + verify the phone number first, then enable the factor with
  // a plain no-body call — it just checks the phone is already verified.
  const startSms = useMutation({
    mutationFn: (phoneNumber: string) =>
      instance.post("/api/users/me/phone", { phone_number: phoneNumber }),
  });

  const confirmSms = useMutation({
    mutationFn: async (code: string) => {
      await instance.post("/api/users/me/phone/verification", { code });
      return instance.post("/api/users/me/mfa/factors/sms");
    },
    onSuccess: invalidateAndMarkEnabled,
  });

  const disableFactor = useMutation({
    mutationFn: (factor: MfaFactor) =>
      instance.delete(`/api/users/me/mfa/factors/${factor}`),
    onSuccess: invalidate,
  });

  return {
    startTotp,
    confirmTotp,
    startSms,
    confirmSms,
    disableFactor,
  };
};
